/**
 * Deterministic Guid constants matching backend EtlGuid helpers.
 *
 * Otoriter kaynak: services/Wiser-ATS-BE/Data/Seeding/Sql/**
 * Dogrulama: python scripts/audit_fe_guid_constants.py
 *
 * DIKKAT: legacy id'ler sirali degil. TalentSpotStatus 1,3,4,5,6 (2 yok),
 * WorkingStatusParameter 1 ve 3. Yeni deger eklerken seed SQL'i ADIYLA okuyun.
 *
 * BURAYA SIRKET (CompanyParameter) GUID'I EKLEMEYIN. Sirket kiraci verisidir,
 * urun sabiti degil. Kullanicinin sirketi GetMyDetail -> effectiveCompanyId ile
 * gelir; backend bunu son departmanin CompanyID'si, yoksa
 * InstanceSettings.DefaultCompanyId olarak cozumler.
 * Bkz. services/ats/docs/multitenancy/MULTITENANCY_RDC_COUPLING.md §2.1, §13.
 */

export const ProjectStatusParameterGuids = {
  // Seed SQL ile birebir aynı; denetimle doğrulanmıştır (4/4).
  // Eski not "C# EtlGuid farklı hash üretiyor" diyordu — artık geçerli değil,
  // EtlGuid.Create ve seed SQL aynı uuid5(NAMESPACE_DNS) değerini veriyor.
  aktif: "7544ec38-7d39-5f89-bbb4-18683599cd6e",
  bekliyor: "0c19d94c-8f2a-54ca-a8e5-650754fa2ce3",
  iptal: "8b1ab4fa-454d-5cca-8503-c7a8d43de6a4",
  kapali: "7d3fc712-48fa-5e70-8b06-b119f78697b0",
} as const;

/**
 * DIKKAT: bu grubun otoriter kaynagi seed SQL DEGIL.
 * Priority bir parametre tablosu degil, `Enums.Priority` enum'u; Guid'ler
 * `Helper/ParameterGuids/PriorityParameterGuids.cs` icinde
 * `EtlGuid.Create("Priority", n)` ile uretiliyor ve `GET api/Data/GetPriority`
 * bunlari donduruyor (`DataService.ParameterLists.cs:446`).
 *
 * Onceki uc deger uydurmaydi (surum nibble'lari f, c, 3 — uuid5 bile degil),
 * bu yuzden 6 karsilastirma yeri hicbir zaman tutmuyordu. Bkz. Azure task 11581.
 */
export const PriorityParameterGuids = {
  high: "a4646d65-1f12-5789-a5bb-6082c68ff39b", // 1 Yüksek
  medium: "9a04504c-93ce-59f8-9a70-eeda4efd2e73", // 2 Orta
  low: "77164762-8bfe-587d-a780-d776b2e3512a", // 3 Düşük
} as const;

export const PositionDifficultyLevelGuids = {
  high: "d3e7ea6d-6740-55eb-8659-6295dd23b6dc", // 1 Yüksek
  medium: "49ae0af7-1193-57f0-893c-d8f4e7c9ea90", // 2 Orta
} as const;

export const TalentSpotStatusGuids = {
  olusturuldu: "b6f143e1-1f63-5245-a682-23219eca0c90", // 1 Created
  basladi: "ccf51fd3-07db-5df0-bc32-3ef276cd4cae", // 3 Started
  tamamlandi: "44bad5ce-0f48-5739-924d-37e1b5f6221a", // 4 Completed
  iptalEdildi: "bd548880-4c3b-5e6a-b075-c7618b055797", // 5 Cancelled
  straightLining: "acc66a4e-5b94-5ce7-beb9-b3b63ad4a7e2", // 6 StraightLiningDetected
} as const;

export const WorkingStatusParameterGuids = {
  employed: "aeaa0604-2286-5472-8a88-d1c43f46cb49", // 1 ÇALIŞIYOR
  unemployed: "8a55a567-653f-5b6a-a801-c4c7ae1b8da8", // 3 ÇALIŞMIYOR
} as const;

/**
 * Parameter.CandidateSource — StateInfo.CandidateSourceID'nin FK'si.
 * Upstream (`ed67d8e`, `16bed52`) bunlari ham int (1/2) kullaniyor.
 * Seed: services/Wiser-ATS-BE/Data/Seeding/Sql/system/200_CandidateSource.sql
 */
export const CandidateSourceGuids = {
  wiserAi: "9ff6609a-8ecf-58f5-851c-936579bedf30", // 1 Wiser AI
  diger: "e41e28b1-5aa8-5654-875a-683510d07355", // 2 Diğer
} as const;

/**
 * Parameter.ExchangeParameter — SkillInfo/StateInfo ücret birimi FK'si.
 * Seed: services/Wiser-ATS-BE/Data/Seeding/Sql/system/090_ExchangeParameter.sql
 *
 * Upstream 5f73b07 `DEFAULT_EXCHANGE_PARAMETER_ID = 3` (Türk Lirası) sabitini
 * ham int olarak kullanıyordu.
 */
export const ExchangeParameterGuids = {
  usd: "b5f0d574-4014-5dbf-a74f-ae780cec4718", // 1 Amerikan Doları
  eur: "f8a191a6-a2cb-53d1-b798-31bc93cc6623", // 2 Euro
  // `try` JavaScript'te ayrılmış sözcük olduğu için `tryy`.
  tryy: "660465af-0c64-51e5-b112-256eecae3ee9", // 3 Türk Lirası
  gbp: "a380a88d-d005-55d9-b7dc-846f0d656dd4", // 4 İngiliz Sterlini
} as const;

/** Ücret girilip birim seçilmediğinde uygulanan varsayılan. */
export const DEFAULT_EXCHANGE_PARAMETER_GUID = ExchangeParameterGuids.tryy;

export const isFilledGuid = (value: unknown): value is string =>
  typeof value === "string" &&
  value.length > 0 &&
  value !== "00000000-0000-0000-0000-000000000000";
